/**
 * @fileoverview TanStack Query client configuration and API utilities.
 * Provides the QueryClient instance with default options, a generic
 * apiRequest function for mutations, and a query function factory
 * for fetching data with authentication.
 * @module lib/queryClient
 */

import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { API_BASE_URL } from "@/config";

/**
 * Throws an error if the response is not OK (status 200-299).
 * Extracts error message from response body or status text.
 * @param res - Fetch Response object
 * @throws Error with status code and message
 */
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Makes an authenticated API request.
 * Automatically adds Authorization header from localStorage token
 * and Content-Type header for requests with body data.
 * @param method - HTTP method (GET, POST, PATCH, DELETE, etc.)
 * @param url - API endpoint URL (relative or absolute)
 * @param data - Optional request body data (will be JSON stringified)
 * @returns Fetch Response object
 * @throws Error if response is not OK
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("authToken") : null;
  
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

/** Behavior options for handling 401 unauthorized responses */
type UnauthorizedBehavior = "returnNull" | "throw";

/**
 * Creates a query function for TanStack Query with configurable 401 handling.
 * Query key segments are joined to form the API endpoint path.
 * Automatically includes Authorization header from localStorage.
 * @param options - Configuration options
 * @param options.on401 - Behavior on 401: "returnNull" returns null, "throw" throws error
 * @returns QueryFunction for use with useQuery
 */
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const path = queryKey.join("/") as string;
    const fullUrl = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("authToken") : null;
    
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const res = await fetch(fullUrl, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * Configured TanStack Query client instance.
 * Default options:
 * - Throws on 401 unauthorized
 * - No automatic refetching
 * - Infinite stale time (manual invalidation only)
 * - No retry on failure
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
